import { NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { GET_OPERATIONAL_COSTS } from 'features/OperationalCosts/queries'
import { OperationalCostsList } from 'features/OperationalCosts/components/OperationalCostsList'
import { Loader } from '@10xLabs/react-web/components/Loader'
import EmptyState from 'components/v2/EmptyState'
import { getQueries } from 'utils/route'
import { PaginationList } from 'components/PaginationList'
import { OperationalCost, OperationalCostVehicleType } from 'gql/generated/graphql'
import { Button } from 'components/v2/Button'
import { createOperationalCostsCollection } from 'features/OperationalCosts/models'
import { IOperationalCostsFilters } from 'features/OperationalCosts/types'
import { filterEmptyValue } from 'utils/object'
import { isEmpty } from 'lodash'
import { NavLink } from 'react-router-dom'

const LIMIT = 30

interface IOperationalCostsContainerProps {
  filters: IOperationalCostsFilters
  vehicleTemplateLoading: boolean
}

export const OperationalCostsContainer = ({
  filters,
  vehicleTemplateLoading
}: IOperationalCostsContainerProps): ReactElement => {
  const queries = getQueries(location.search)
  const { page = '1' } = queries
  const pageNb = parseInt(page)

  const filtersInput = {
    businessClientID: filters.businessClient?.id || undefined,
    businessSiteID: filters.businessSite?.id || undefined,
    stateCode: filters.state?.code || undefined,
    vehicleCapacity: filters.vehicleTemplate?.capacity || undefined,
    vehicleType: (filters.vehicleTemplate?.type?.toUpperCase() as OperationalCostVehicleType) || undefined
  }
  const hasFiltersApplied = !isEmpty(filterEmptyValue(filtersInput))

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_OPERATIONAL_COSTS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: vehicleTemplateLoading,
    variables: {
      input: filtersInput,
      pagination: {
        page: pageNb,
        size: LIMIT
      }
    }
  })
  const operationalCostsCollection = useMemo(() => {
    return createOperationalCostsCollection((data?.operationalCosts.items as OperationalCost[]) || [])
  }, [data])

  if (vehicleTemplateLoading || loading || networkStatus === NetworkStatus.refetch) {
    return (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  }

  if (error) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="operationalCosts.error"
            defaultMessage="An error occurred while fetching operational costs. Please try again."
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
          >
            <FormattedMessage id="operationalCosts.retry" defaultMessage="Retry" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  }

  if (!data?.operationalCosts?.items?.length && !hasFiltersApplied) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage id="operationalCosts.empty" defaultMessage="No operational costs found." />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  }

  if (!data?.operationalCosts?.items?.length && hasFiltersApplied) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="operationalCosts.noSearchResults"
            defaultMessage="No operational costs found with the applied filters."
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button as={NavLink} to="/operational-costs">
            <FormattedMessage id="operationalCosts.resetFilters" defaultMessage="Reset filters" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  }

  return (
    <>
      <OperationalCostsList operationalCostsCollection={operationalCostsCollection} />
      <PaginationList
        page={pageNb}
        limit={LIMIT}
        totalItems={data?.operationalCosts.total || 0}
        currentItemsCount={data?.operationalCosts.items.length || 0}
        withPaddingVertical
      />
    </>
  )
}
